import React, { useEffect, useState } from 'react';
import { ShieldCheck, UserCheck, X } from 'lucide-react';
import useAuthStore from '../../store/authStore';

interface LoginSuccessModalProps {
  onClose: () => void;
}

const LoginSuccessModal: React.FC<LoginSuccessModalProps> = ({ onClose }) => {
  const { user, isAdmin } = useAuthStore();
  const [visible, setVisible] = useState(true);

  // Auto-close after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(onClose, 300); // Wait for fade-out animation
    }, 5000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300); // Wait for fade-out animation
  };

  if (!visible) {
    return null;
  }

  return (
    <div className="fixed inset-0 flex items-start justify-center z-50 pt-16 px-4">
      <div 
        className={`bg-white rounded-lg shadow-xl max-w-md w-full transform transition-all duration-300 ease-in-out ${
          visible ? "translate-y-0 opacity-100" : "-translate-y-4 opacity-0"
        }`}
      >
        <div className={`p-4 rounded-t-lg flex items-center justify-between ${
          isAdmin ? 'bg-green-50' : 'bg-blue-50'
        }`}>
          <div className="flex items-center">
            {isAdmin ? (
              <ShieldCheck className={`h-5 w-5 mr-2 text-green-600`} />
            ) : (
              <UserCheck className={`h-5 w-5 mr-2 text-blue-600`} />
            )}
            <span className={`font-medium ${isAdmin ? 'text-green-800' : 'text-blue-800'}`}>
              {isAdmin ? 'Admin Login Successful' : 'Login Successful'}
            </span>
          </div>
          <button 
            onClick={handleClose} 
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        
        <div className="p-4">
          <p className="mb-2">
            <span className="font-medium">Welcome, {user?.firstName || 'User'}</span>
          </p>
          <p className="text-sm text-gray-600">
            {isAdmin 
              ? 'You have administrator privileges. You can access the admin dashboard and manage products, orders, and customers.'
              : 'You can now access your account, make purchases, and track your orders.'
            }
          </p>
          <div className="mt-4 text-xs text-gray-500">
            This notification will automatically close in a few seconds.
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginSuccessModal;
